import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from "react-router-dom";
import styled from 'styled-components'
import { Amount, Panel, Layout, Stat, LazyBoi, GateKeeper, Field, Alert, Toggle } from '@components'
import { Asset } from '@archetypes'
import { MintController } from './_controller'
import appController, { Status } from '@app/App.Controller'
import { ReactComponent as Refresh } from '@assets/Icon_Refresh.svg';
import useInterval from "../../hooks/useInterval"
import { fromWei, BN } from '@util/helpers'

const Wrapper = styled.div`
	padding-left: 20px;
	padding-right: 20px;

	.group-input {
		margin-bottom: 30px;
	}

`



const MainOLD = styled(
	({ address, ...rest }) => {

		const { synthetics } = appController

		// approval params
		const [approvalStatus, setApprovalStatus] = useState()
		const [approvalParams, setApprovalParams] = useState()
		const [controller, setController] = useState()
		const [mintParams, setMintParams] = useState()
		const [timestamp, setTimestamp] = useState(new Date().valueOf())

		useEffect(() => {
			const mintController = new MintController(address, appController)
			mintController.on('approvals', setApprovalParams)
			mintController.on('params', setMintParams)
			setController(mintController)
			mintController.init()

			return () => {
				mintController.cleanup()
			}
		}, [address]) // eslint-disable-line

		// TODO : Make a hook
		useInterval(() => {
			const diff = new Date().valueOf() - timestamp
			if (diff > 2000) {
				// Validate the input amount
				if (controller && mintParams && mintParams.token.amount) {
					if (mintParams.token.min > mintParams.token.amount) {
						controller.updateInputAmount(mintParams.token.min)
					} else if (mintParams.token.amount > mintParams.token.max) {
						controller.updateInputAmount(mintParams.token.max)
					}
				}
			}
		}, 1000);

		return <Panel fadeIn {...rest}>
			<GateKeeper
				controllerAssertions={[
					{
						controller: 'account',
						key: 'status',
						assert: val => val === Status.READY,
						onFail: <Panel fadeIn>Connect your account to mint assets</Panel>,
					},
				]}
				assertions={[
					{
						assert: () => !!mintParams,
						onFail: () => <Panel fadeIn><Refresh className='rotate' />Fetching asset info</Panel>,
					},
					{
						assert: () => (Number(mintParams?.collateral?.max) >= Number(mintParams?.collateral?.min)),
						onFail: () => <Panel fadeIn>Not Enough PERL to mint. You need at least {mintParams?.collateral?.min} PERL. </Panel>,
					}
				]}
				dependencies={[mintParams]}
			>

				<Amount
					title='Tokens to mint'
					info={
						<LazyBoi
							prefix={'Max:'}
							value={mintParams?.token?.max}
							suffix={mintParams?.token?.symbol}
							dp={2}
						/>
					}
					value={mintParams?.token?.amount}
					onChange={val => {
						setTimestamp(new Date().valueOf())
						return controller?.updateInputAmount(val)
					}}
					max={mintParams?.token?.max}
				/>
				<Panel.Filler>
					+
				</Panel.Filler>

				{!mintParams?.noPosition && (
					<Field.RadioGroup
						title={'Do you want to add more collateral or use existing?'}
						value={mintParams?.options}
						options={[
							{
								label: 'Add more collateral',
								value: true
							},
							{
								label: 'Use existing collateral',
								value: false
							}
						]}
						onChange={val => controller?.updateOptions(val)}
					/>
				)
				}

				{(() => {
					const amount = mintParams?.collateral?.amount
					const symbol = mintParams?.collateral?.symbol
					const max = mintParams?.collateral?.max
					return (
						<>
							<Amount
								title='Collateral to add (PERL)'
								info={
									<LazyBoi
										prefix={'Max:'}
										value={max}
										suffix={symbol}
										dp={2}
									/>
								}
								value={amount}
								min={0}
								disabled
							/>
						</>
					)
				})()
				}

				<Panel.Divider light>
					<Asset.ContractApproval
						approvals={approvalParams}
						onUpdate={setApprovalStatus}
					/>
				</Panel.Divider>
				<Panel.Footer>
					<Panel.Footer.Group>
						<div style={{ display: "flex", flexDirection: "row" }}>
							<Stat title='New Collaterization Ratio' small>
								<LazyBoi value={mintParams?.newCRUsd ? mintParams.newCRUsd * 100 : 0} suffix={"%"} dp={2} />
							</Stat>
						</div>
					</Panel.Footer.Group>
					<Panel.Footer.Group>
						<Asset.ContractApproval.Button
							{...approvalStatus}
							onClick={() => {
								return synthetics.mint(mintParams?.tx)
							}}
						>
							Mint assets
						</Asset.ContractApproval.Button>
					</Panel.Footer.Group>
				</Panel.Footer>
			</GateKeeper>
		</Panel>
	})`
	
	`

const Main = ({ address, setExtensions, ...rest }) => {

	const { synthetics } = appController

	// approval params
	const [approvalStatus, setApprovalStatus] = useState()
	const [approvalParams, setApprovalParams] = useState()
	const [controller, setController] = useState()
	const [mintParams, setMintParams] = useState()


	useEffect(() => {
		const mintController = new MintController(address, appController)
		mintController.on('approvals', setApprovalParams)
		mintController.on('params', setMintParams)
		setController(mintController)
		mintController.init()

		return () => {
			mintController.cleanup()
		}
	}, [address]) // eslint-disable-line

	useEffect(() => {
        if (mintParams) {
            let payload = []  
            if (mintParams?.debtBalance) { 
                const newDebt = Number(fromWei(mintParams?.debtBalance)) + mintParams?.token?.amount   
                payload.push({
                    key: "Total Debt",
                    value: <LazyBoi
                        value={newDebt}
                        suffix={mintParams?.token?.symbol}
                        dp={2}
                    />
                })
            }

            if (mintParams?.collateralBalance) {
                const newTotalCollateral = Number(fromWei(mintParams?.collateralBalance)) + mintParams?.collateral?.amount   
                payload.push({
                    key: "Total Collateral",
                    value: <LazyBoi
                        value={newTotalCollateral}
                        suffix={mintParams?.collateral?.symbol}
                        dp={2}
                    />
                })
            } 

            if (mintParams?.newCRUsd) {
                payload.push({
                    key: "New Collateral Ratio (CR)",
                    value: <LazyBoi
                        value={mintParams?.newCRUsd * 100}
                        suffix={"%"}
                        dp={0}
                    />
                })
            }
            setExtensions(payload)
        }
    }, [mintParams])

	const toggleOption = useCallback((e) => {
		if (controller) {
			controller.updateOptions(!mintParams?.options)
		}
	}, [mintParams, controller])

	const isTooLow = (mintParams?.token?.min > mintParams?.token?.amount) ? true : false

	return (
		<Panel fadeIn {...rest}>
			<GateKeeper
				controllerAssertions={[
					{
						controller: 'account',
						key: 'status',
						assert: val => val === Status.READY,
						onFail: <Panel fadeIn>Connect your account to mint assets</Panel>,
					},
				]}
				assertions={[
					{
						assert: () => !!mintParams,
						onFail: () => <Panel fadeIn><Refresh className='rotate' />Fetching asset info</Panel>,
					},
					{
						assert: () => (Number(mintParams?.collateral?.max) >= Number(mintParams?.collateral?.min)),
						onFail: () => <Panel fadeIn>Not Enough PERL to mint. You need at least {mintParams?.collateral?.min} PERL. </Panel>,
					}
				]}
				dependencies={[mintParams]}
			>
				<Wrapper>
					<Amount.GroupSelect
						className="group-input"
						info="I WANT TO MINT"
						infoEnd={`Max : ${Number(mintParams?.token?.max).toLocaleString()} ${mintParams?.token?.symbol}`}
						min={0}
						disabled={!controller}
						onChange={amount => controller?.updateInputAmount(amount)}
						max={mintParams?.token?.max}
						selection={
							<Asset.Icon
								token={mintParams?.token?.symbol}
							/>
						}
					/>
					<Alert style={{color: "red"}} visible={isTooLow}>Please enter amount more than {mintParams?.token?.min} {mintParams?.token?.symbol}</Alert>

					{(() => {
						const amount = mintParams?.collateral?.amount
						const symbol = mintParams?.collateral?.symbol
						const max = mintParams?.collateral?.max
						return (
							<Amount.GroupSelect
								className="group-input"
								disabled={true}
								info="WITH A COLLATERAL OF"
								infoEnd={`Max : ${Number(max).toLocaleString()} ${symbol}`}
								min={0}
								value={amount || 0}
								selection={
									<Asset.Icon
										token={symbol}
									/>
								}
							/>
						)
					})()}

					{!mintParams?.noPosition && (
						<div
							style={{ marginBottom: "20px", display: "flex", flexDirection: "row" }}
						>
							<Toggle
								value={mintParams?.options}
								onChange={toggleOption}
							/>
							<div style={{ marginTop: "auto", marginBottom :"auto" , marginLeft : "10px" , color : "var(--color-label, lightblue)" }}>
								Use existing collateral ({Number(mintParams?.maxExistingCollateral).toLocaleString()} {mintParams?.token?.symbol})
							</div>
						</div>

					)}
					<Asset.ContractApproval.Wizard
						approvals={approvalParams}
						onUpdate={setApprovalStatus}
						actionText={"Mint Assets"}
						onAction={() => {
							if (mintParams?.tx?.numTokens !== (0 && undefined)) return synthetics.mint(mintParams?.tx)
						}}
					/>

				</Wrapper>
			</GateKeeper>
		</Panel>)
}


const Mint = ({ className }) => {
	const { address } = useParams();

	return <Layout back title='Mint Assets' className={className}>
		<Layout.Aside>
			<Asset.Position address={address} />
			<Asset.Stats address={address} />
		</Layout.Aside>
		<Layout.Main>
			<Main address={address} />
		</Layout.Main>

	</Layout>
}

Mint.Panel = Main

export default Mint
